<template>
  <span :class="classList">
    <input
      type="checkbox"
      :id="id"
      :name="name"
      :value="value"
      :required="required"
      :disabled="disabled"
      @change="onChange"
      @keydown.enter.prevent="onChange"
      :checked="state"
      :aria-checked="state"
      :tabindex="tabindex"
    />
    <label :for="id">
      <slot name="input-box">
        <span class="input-box">
          <svg
            width="24"
            role="presentation"
            :aria-label="isStoryBook ? 'Checkbox Icon Title' : ariaLabel"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <title>
              {{ isStoryBook ? "Checkbox Icon Title" : ariaLabel }}
            </title>
            <desc>
              {{ isStoryBook ? "Checkbox Icon Description" : iconDescription }}
            </desc>
            <path
              d="M6 12.4058L9.72003 16L18 8"
              stroke="white"
              stroke-width="2.5"
            />
          </svg>
        </span>
      </slot>
      <slot></slot>
    </label>
  </span>
</template>

<script>
import { version } from "vue";

export default {
  name: "st-checkbox",
  model: {
    prop: "modelValue",
    event: "checkbox-input",
  },
  props: {
    id: {
      type: String,
    },
    name: {
      type: String,
      default: null,
    },
    value: {
      default: null,
    },
    modelValue: {
      default: undefined,
    },
    checked: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    tabindex: {
      type: String,
      default: "0",
      required: true,
    },
    model: {},
    ariaLabel: {
      type: String,
      required: true,
    },
    iconDescription: {
      type: String,
      required: true,
    },
    hasNoMarginTop: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    isVue3() {
      return version && parseInt(version) === 3;
    },
    isStoryBook() {
      return process.env.STORYBOOK_SELF;
    },
    classList: function () {
      const checkbox = "st-checkbox";
      return [
        checkbox,
        this.hasNoMarginTop && `${checkbox}--has-no-margin-top`,
      ];
    },
    state: function () {
      if (this.modelValue === undefined) {
        return this.checked;
      }

      if (Array.isArray(this.modelValue)) {
        return this.modelValue.indexOf(this.value) > -1;
      }

      return !!this.modelValue;
    },
  },

  methods: {
    onChange: function () {
      this.toggle();
    },

    toggle: function () {
      let value;

      if (Array.isArray(this.modelValue)) {
        value = this.modelValue.slice(0);

        if (this.state) {
          value.splice(value.indexOf(this.value), 1);
        } else {
          value.push(this.value);
        }
      } else {
        value = !this.state;
      }

      if (!this.isVue3) this.$emit("checkbox-input", value);
    },
  },

  watch: {
    checked: function (newValue) {
      if (newValue !== this.state) {
        this.toggle();
      }
    },
  },

  mounted: function () {
    if (this.checked && !this.state) {
      this.toggle();
    }
  },
};
</script>

<style lang="scss" scoped>
@import "../../scss/main.scss";

.st-checkbox {
  // We have a span selector in main.scss in organization that overwrites the font-size of span:s in components
  font-size: 1em !important;
  margin-top: 1.5625em;
  margin-left: 0.313em;
  display: block;
  position: relative;
  font-family: $source-sans-pro, sans-serif;
  &--has-no-margin-top {
    margin-top: 0;
  }

  input[type="checkbox"] ~ label:before {
    content: "";
  }

  > input {
    opacity: 0;
    position: absolute;
    top: 0.375em;
    left: 0.25em;

    + label > .input-box {
      // We have a span selector in main.scss in organization that overwrites the font-size of span:s in components
      font-size: 1em !important;
      box-sizing: border-box;
      display: flex;
      justify-content: center;
      align-items: center;
      border-radius: 0.1875em;
      border: solid 0.125em #6e6055;
      margin: 0;
      padding: 0;
      width: 1.5em;
      height: 1.5em;
      background: $vit;
      overflow: hidden;
      margin-right: 0.75em;
      user-select: none;

      > .input-box-tick {
        width: 100%;
        height: 100%;
        margin-top: 0.25em;

        > path {
          opacity: 0;
          stroke-width: 0.125em;
          stroke-dashoffset: 18;
          stroke-dasharray: 18;
        }
      }
    }

    &:focus + label > .input-box {
      opacity: 1;
      box-shadow: 0 0 0 0.125em $vit, 0 0 0 0.25em $varm-grå;
      outline: 0;
    }

    &:checked + label > .input-box {
      background-color: #6e6055;
    }

    &:checked + label > .input-box > .input-box-tick > path {
      opacity: 1;
      stroke-dashoffset: 0;
      stroke: $vit;
    }

    &:disabled + label {
      opacity: 0.7;
    }
  }

  label {
    font-size: 1em;
    font-weight: normal;
    font-style: normal;
    font-stretch: normal;
    line-height: 1.5;
    letter-spacing: normal;
    color: $grey-dark;
    display: flex;
    align-items: center;
  }
}
</style>
